import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Table } from '../../components/Table';
import { useSession } from '../../lib/useSession';
import { describeApiError } from '../qrs/errors';
import { itemsOf, useApiQuery } from '../qrs/hooks';
import { permissionsFor } from '../qrs/permissions';
import { QrFiltersBar } from '../qrs/QrFiltersBar';
import { QrTable } from '../qrs/QrTable';
import { formatNumber, roleLabel, STATE_DOT, STATE_LABEL, STATE_ORDER } from '../qrs/states';
import type { Inventory, Lot, QrFilters } from '../qrs/types';
import { useMembers } from '../qrs/useMemberNames';
import { useOrgApi } from '../qrs/useOrgApi';
import { useQrList } from '../qrs/useQrList';
import { CreateLotModal } from '../lots/CreateLotModal';

export function InventoryPage() {
  const client = useOrgApi();
  const { activeOrg } = useSession();
  const perms = permissionsFor(activeOrg?.role);
  const [filters, setFilters] = useState<QrFilters>({ kind: 'physical' });
  const [creating, setCreating] = useState(false);

  const inventory = useApiQuery((signal) => client.get<Inventory>('/v1/inventory', { signal }), [client]);
  const lots = useApiQuery((signal) => client.get<{ items: Lot[] } | Lot[]>('/v1/lots', { signal }).then(itemsOf), [client]);
  const detail = useQrList(client, filters);
  const { members, names } = useMembers(client, perms.manageLots);
  const roles = Object.fromEntries(members.map((m) => [m.uid, m.role]));
  const custodianRows = (inventory.data?.byCustodian ?? []).map((b) => ({
    uid: b.uid,
    name: names[b.uid] ?? b.uid,
    role: roles[b.uid],
    inCustody: b.counts.en_custodia ?? 0,
    activated: (b.counts.activado ?? 0) + (b.counts.suspendido ?? 0),
  }));

  const counts = inventory.data?.byState ?? {};
  const total = STATE_ORDER.reduce((acc, s) => acc + (counts[s] ?? 0), 0);

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">Inventario de QR físicos</h1>
          <p className="text-sm text-ink-muted">{perms.ownOnly ? 'Los QR que tienes en custodia y los que activaste.' : 'Estado de cada calcomanía, desde la imprenta hasta el negocio.'}</p>
        </div>
        {perms.manageLots && (
          <div className="flex gap-2">
            <Link to="/consola/lotes" className="inline-flex items-center rounded-md border border-line bg-white px-4 py-2 text-sm font-semibold text-ink no-underline hover:bg-mist">
              Asignar custodia
            </Link>
            <Button onClick={() => setCreating(true)}>Pedir lote</Button>
          </div>
        )}
      </div>

      {inventory.error ? (
        <Banner tone="error" action={<Button variant="secondary" size="sm" onClick={inventory.reload}>Reintentar</Button>}>
          {describeApiError(inventory.error, 'No pudimos cargar el inventario.')}
        </Banner>
      ) : null}

      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" aria-label="QR por estado">
        {STATE_ORDER.map((s) => {
          const n = counts[s] ?? 0;
          return (
            <li key={s} className="rounded-lg border border-line bg-white p-3">
              <div className="flex items-center gap-1.5 text-xs text-ink-muted">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: STATE_DOT[s] }} aria-hidden="true" />
                {STATE_LABEL[s]}
              </div>
              {inventory.loading ? (
                <div className="mt-1 h-7 w-12 animate-pulse rounded bg-gray-100" />
              ) : (
                <div className={`font-display text-2xl font-semibold tabular-nums ${n === 0 ? 'text-gray-400' : 'text-ink'}`}>{formatNumber(n)}</div>
              )}
            </li>
          );
        })}
      </ul>

      {!inventory.loading && !inventory.error && total === 0 ? (
        <EmptyState
          title={perms.ownOnly ? 'Aún no tienes QR en custodia' : 'Todavía no hay QR físicos'}
          description={perms.ownOnly ? 'Cuando un Admin te asigne calcomanías, aparecerán aquí.' : 'Pide tu primer lote: reservamos los códigos y te dejamos el kit listo para la imprenta.'}
          action={perms.manageLots ? <Button onClick={() => setCreating(true)}>Pide tu primer lote</Button> : undefined}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          {!perms.ownOnly && (
            <div className="space-y-2 lg:col-span-1">
              <h2 className="font-display text-base font-bold text-ink">Por custodio</h2>
              <Table<(typeof custodianRows)[number]>
                caption="QR por custodio"
                loading={inventory.loading}
                rows={custodianRows}
                rowKey={(r) => r.uid}
                empty="Nadie tiene QR en custodia."
                columns={[
                  {
                    key: 'who',
                    header: 'Custodio',
                    render: (r) => (
                      <span>
                        {r.name} {r.role && <span className="text-xs text-ink-muted">{roleLabel(r.role)}</span>}
                      </span>
                    ),
                  },
                  { key: 'custody', header: 'En custodia', align: 'right', render: (r) => <span className="tabular-nums">{formatNumber(r.inCustody)}</span> },
                  { key: 'activated', header: 'Activados', align: 'right', render: (r) => <span className="tabular-nums">{formatNumber(r.activated)}</span> },
                ]}
              />
            </div>
          )}
          <div className={`space-y-2 ${perms.ownOnly ? 'lg:col-span-3' : 'lg:col-span-2'}`}>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="mr-auto font-display text-base font-bold text-ink">Detalle</h2>
              <QrFiltersBar value={filters} onChange={(f) => setFilters({ ...f, kind: 'physical' })} lots={lots.data} showKind={false} searchPlaceholder="Buscar código o referencia" />
            </div>
            {detail.error ? (
              <Banner tone="error" action={<Button variant="secondary" size="sm" onClick={detail.reload}>Reintentar</Button>}>
                {describeApiError(detail.error, 'No pudimos cargar el detalle.')}
              </Banner>
            ) : (
              <QrTable caption="Detalle de QR físicos" loading={detail.loading} rows={detail.data?.items ?? []} columns={['code', 'lot', 'state', 'custodian', 'business', 'actions']} names={names} empty="Ningún QR calza con los filtros." />
            )}
          </div>
        </div>
      )}

      <CreateLotModal open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); inventory.reload(); lots.reload(); }} />
    </section>
  );
}
